import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { Provider } from 'react-redux';
import store from './store/store';
import esriLogger from '@arcgis/core/core/Logger';
import { logSessionHeader } from '@origo/logger';
import { getCustomerDomain } from '@origo/auth';
import { installWireRecorder } from '@origo/arcgis';
import './index.css';
import '../../packages/origo-ui/index.css';
import '../../packages/origo-ui/dist/style.css';
import '../../packages/origo-components/dist/style.css';

// Demp ArcGIS-deprecations i prod. Behold warnings i dev så vi fanger dem
// opp når vi migrerer widgets til web-components.
esriLogger.getLogger('esri').level = import.meta.env.PROD ? 'error' : 'warn';

logSessionHeader({
    app: 'drømmeplan-admin',
    version: __APP_VERSION__,
    commit: __APP_COMMIT__,
    customer: getCustomerDomain(),
    buildDate: __APP_BUILD_DATE__,
});

// Record-modus for testdata (etappe 0): fanger parsede ArcGIS-responser til
// test-recordings/ via dev-serveren. Statisk falsk i prod-bygg → tree-shakes.
if (import.meta.env.DEV && import.meta.env.VITE_RECORD_WIRE === '1') {
    installWireRecorder({
        app: 'drømmeplan-admin',
        label: import.meta.env.VITE_RECORD_LABEL,
    });
}

// All calcite web components import in one place
import "@esri/calcite-components/dist/components/calcite-switch";
import "@esri/calcite-components/dist/components/calcite-panel";
import "@esri/calcite-components/dist/components/calcite-block";
import "@esri/calcite-components/dist/components/calcite-block-section";
import "@esri/calcite-components/dist/components/calcite-card";
import "@esri/calcite-components/dist/components/calcite-card-group";
import "@esri/calcite-components/dist/components/calcite-popover";
import "@esri/calcite-components/dist/components/calcite-pagination";

import "@esri/calcite-components/dist/components/calcite-stepper";
import "@esri/calcite-components/dist/components/calcite-stepper-item";

import "@esri/calcite-components/dist/components/calcite-sheet";
import "@esri/calcite-components/dist/components/calcite-menu";
import "@esri/calcite-components/dist/components/calcite-menu-item";
import '@esri/calcite-components/dist/components/calcite-button';
import '@esri/calcite-components/dist/components/calcite-split-button';
import '@esri/calcite-components/dist/components/calcite-fab';
import '@esri/calcite-components/dist/components/calcite-segmented-control';
import '@esri/calcite-components/dist/components/calcite-segmented-control-item';
import '@esri/calcite-components/dist/components/calcite-label';
import '@esri/calcite-components/dist/components/calcite-checkbox';
import '@esri/calcite-components/dist/components/calcite-chip';
import '@esri/calcite-components/dist/components/calcite-chip-group';
import '@esri/calcite-components/dist/components/calcite-radio-button';
import '@esri/calcite-components/dist/components/calcite-radio-button-group';
import '@esri/calcite-components/dist/components/calcite-tooltip';
import '@esri/calcite-components/dist/components/calcite-icon';
import '@esri/calcite-components/dist/components/calcite-alert';
import '@esri/calcite-components/dist/components/calcite-input';
import '@esri/calcite-components/dist/components/calcite-input-text';
import '@esri/calcite-components/dist/components/calcite-input-number';
import '@esri/calcite-components/dist/components/calcite-slider';
import '@esri/calcite-components/dist/components/calcite-text-area';
import '@esri/calcite-components/dist/components/calcite-dialog';
import '@esri/calcite-components/dist/components/calcite-color-picker';

import '@esri/calcite-components/dist/components/calcite-list';
import '@esri/calcite-components/dist/components/calcite-list-item';
import '@esri/calcite-components/dist/components/calcite-list-item-group';
import '@esri/calcite-components/dist/components/calcite-accordion';
import '@esri/calcite-components/dist/components/calcite-accordion-item';

import '@esri/calcite-components/dist/components/calcite-combobox';
import '@esri/calcite-components/dist/components/calcite-combobox-item';
import '@esri/calcite-components/dist/components/calcite-combobox-item-group';
import '@esri/calcite-components/dist/components/calcite-filter';

import '@esri/calcite-components/dist/components/calcite-dropdown';
import '@esri/calcite-components/dist/components/calcite-dropdown-group';
import '@esri/calcite-components/dist/components/calcite-dropdown-item';

import '@esri/calcite-components/dist/components/calcite-tree';
import '@esri/calcite-components/dist/components/calcite-tree-item';

import "@esri/calcite-components/dist/components/calcite-link";
import "@esri/calcite-components/dist/components/calcite-modal";
import "@esri/calcite-components/dist/components/calcite-progress";
import "@esri/calcite-components/dist/components/calcite-scrim";
import "@esri/calcite-components/dist/components/calcite-notice";

import '@esri/calcite-components/dist/components/calcite-table';
import '@esri/calcite-components/dist/components/calcite-table-header';
import '@esri/calcite-components/dist/components/calcite-table-row';
import '@esri/calcite-components/dist/components/calcite-table-cell';

import '@esri/calcite-components/dist/components/calcite-tabs';
import '@esri/calcite-components/dist/components/calcite-tab-nav';
import '@esri/calcite-components/dist/components/calcite-tab-title';
import '@esri/calcite-components/dist/components/calcite-tab';

import '@esri/calcite-components/dist/components/calcite-input-date-picker';
import '@esri/calcite-components/dist/components/calcite-date-picker';
import '@esri/calcite-components/dist/components/calcite-date-picker-month';

import '@esri/calcite-components/dist/components/calcite-select';
import '@esri/calcite-components/dist/components/calcite-option';

// Initialize i18n with merged resources from packages and app
import { createI18n } from '@origo/i18n';
import { uiTranslations } from '@origo/origo-components';
import appNb from './translations/nb.json';
import appEn from './translations/en.json';

export const i18n = createI18n({
    defaultLanguage: 'nb',
    resources: [
        uiTranslations,
        { nb: { translation: appNb }, en: { translation: appEn } }
    ]
});

import '@arcgis/core/assets/esri/themes/light/main.css';

import { AppErrorBoundary } from './components/AppErrorBoundary';

ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
        <AppErrorBoundary>
            <Provider store={store}>
                <App />
            </Provider>
        </AppErrorBoundary>
    </React.StrictMode>
);
